<script setup lang="ts">
/**
 * 문서 안 [[변수]] — 인라인 편집.
 *   · 일반/인물: 클릭 편집, 인물·회사는 더블클릭 시 챗봇 lookup
 *   · 날짜: 📅 아이콘 / 더블클릭 → 달력
 *   · 주소: 📍 맵 마커 → 챗봇 주소 입력
 *   · currency: 콤보 선택
 *   · yesno: Yes / No 토글
 */
import { computed, inject, onBeforeUnmount, onMounted, ref, watch, nextTick } from 'vue';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import {
  getB04Options,
  loadCmBaseCodeOptions,
} from '@/features/startTask/preset_task/sg/composables/cmBaseCodeOptions';
import { useCddFill, DOC_SCOPE, typeOf } from './useCddFill';

import { formatDateOnly, normalizeDateInputToIso } from '@/shared/composables/dateUtils';
import { useSessionStore } from '@/shared/stores/sessionStore';

const props = defineProps<{ fieldKey: string; compact?: boolean; label?: string }>();
/** 날짜는 ISO(YYYY-MM-DD)로 저장 → 화면은 세션 dateFormat 으로 표시(타임존 변환 없음). */
function toDateDisplay(v: string): string {
  if (!v) return '';
  // 저장값이 ISO/표시형 어느 쪽이든 세션 포맷으로 재표시 (헤더 dateFormat 변경 즉시 반영)
  return formatDateOnly(v) || v;
}
function toDateStorage(v: string): string {
  if (!v) return '';
  const fmt = _dfSession.user?.user?.dateFormat || '';
  const iso = normalizeDateInputToIso(v, fmt);
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : v;
}
// 상단 헤더 dateFormat 변경에 반응하도록 세션 dateFormat 을 추적.
const _dfSession = useSessionStore();
const _dfFormat = computed(() => _dfSession.user?.user?.dateFormat || '');
const fill = useCddFill();
const scope = inject(DOC_SCOPE, '');
/** CSP letterhead 등은 DocScope(cs_1/ …) 와 무관한 공통 키 */
const SHARED_FIELD = /^(csp_)/;
const realKey = computed(() =>
  SHARED_FIELD.test(props.fieldKey) ? props.fieldKey : scope + props.fieldKey,
);

onMounted(() => fill.registerKey(realKey.value));
onBeforeUnmount(() => fill.unregisterKey(realKey.value));

const value = computed(() => fill.values[realKey.value] || '');
const label = computed(() => props.label || fill.labelOf(realKey.value));
const active = computed(() => fill.activeKey.value === realKey.value);
const fieldType = computed(() => typeOf(realKey.value));
const isDate = computed(() => fieldType.value === 'date');
const isAddress = computed(() => fieldType.value === 'address');
const isSsic = computed(() => fieldType.value === 'ssic');
const isLookup = computed(
  () => fieldType.value === 'person' || fieldType.value === 'entity' || fieldType.value === 'ssic',
);
const isCurrency = computed(() => fieldType.value === 'currency');
const isShareType = computed(() => fieldType.value === 'sharetype');
const isNationality = computed(() => fieldType.value === 'nationality');
const isYesNo = computed(() => fieldType.value === 'yesno');

const ed = ref<HTMLElement | null>(null);
const dateMenu = ref(false);

const currencyItems = ref<string[]>(['SGD', 'USD', 'MYR', 'EUR', 'GBP', 'HKD', 'CNY', 'JPY']);
const currencyLoaded = ref(false);
const shareTypeItems = ref<string[]>(['Ordinary', 'Preference', 'Others']);
const shareTypeLoaded = ref(false);
/** 국적 — CM 공통코드 B04 표시명 목록 */
const nationalityItems = ref<string[]>([]);
const nationalityLoaded = ref(false);

async function loadNationalityItems() {
  if (nationalityLoaded.value) return;
  nationalityLoaded.value = true;
  try {
    // 이미 로드돼 있으면 캐시 사용, 아니면 한 번 불러온다.
    const cached = getB04Options();
    const options = cached.length ? cached : await loadCmBaseCodeOptions('B04');
    const names = options.map((o) => o.title).filter((s) => !!s);
    if (names.length) nationalityItems.value = names;
  } catch {
    /* 실패 시 직접 입력만 가능 */
  }
}

async function loadCurrencyItems() {
  if (currencyLoaded.value) return;
  currencyLoaded.value = true;
  try {
    const response = await baseCodeApi.getCodesByMainCode('CM', 'B08', undefined);
    const codes = (response as any).codes || (response as any).data?.codes || [];
    const set = new Set<string>(currencyItems.value);
    for (const code of codes) {
      const total = String(code.totalCode || code.TOTAL_CODE || '').trim();
      if (!total || total.slice(-3) === '000') continue;
      const bigo1 = String(code.bigo1 || code.BIGO1 || '').trim();
      const bigo3 = String(code.bigo3 || code.BIGO3 || '').trim();
      const sym = (bigo1 || bigo3 || '').trim();
      if (sym) set.add(sym);
    }
    const prefer = ['SGD', 'USD', 'MYR', 'EUR', 'GBP'];
    currencyItems.value = [...set].sort((a, b) => {
      const ai = prefer.indexOf(a.toUpperCase());
      const bi = prefer.indexOf(b.toUpperCase());
      if (ai >= 0 && bi < 0) return -1;
      if (ai < 0 && bi >= 0) return 1;
      if (ai >= 0 && bi >= 0) return ai - bi;
      return a.localeCompare(b);
    });
  } catch {
    /* keep defaults */
  }
}

async function loadShareTypeItems() {
  if (shareTypeLoaded.value) return;
  shareTypeLoaded.value = true;
  try {
    const response = await baseCodeApi.getCodesByMainCode('CM', 'A20', undefined);
    const codes = (response as any).codes || (response as any).data?.codes || [];
    const names: string[] = [];
    for (const code of codes) {
      const total = String(code.totalCode || code.TOTAL_CODE || '').trim();
      if (!total || total.slice(-3) === '000') continue;
      const name = String(code.codeName || code.CODE_NAME || code.name || '').trim();
      if (name) names.push(name);
    }
    if (names.length) shareTypeItems.value = names;
  } catch {
    /* keep Ordinary / Preference / Others */
  }
}

onMounted(() => {
  if (isCurrency.value) void loadCurrencyItems();
  if (isShareType.value) void loadShareTypeItems();
  if (isNationality.value) void loadNationalityItems();
  if (ed.value) ed.value.textContent = toDateDisplay(value.value);
});
watch(isCurrency, (v) => {
  if (v) void loadCurrencyItems();
});
watch(isShareType, (v) => {
  if (v) void loadShareTypeItems();
});
watch(isNationality, (v) => {
  if (v) void loadNationalityItems();
});
watch(value, async (v) => {
  await nextTick();
  const disp = toDateDisplay(v);
  if (ed.value && document.activeElement !== ed.value && ed.value.textContent !== disp) {
    ed.value.textContent = disp;
  }
});
// 헤더에서 날짜 형식을 바꾸면 이미 표시된(바인딩된) 날짜도 세션 형식으로 다시 렌더한다.
watch(_dfFormat, async () => {
  await nextTick();
  if (ed.value && document.activeElement !== ed.value) {
    ed.value.textContent = toDateDisplay(value.value);
  }
});

function onInput() {
  if (!ed.value) return;
  const txt = ed.value.textContent || '';
  if (!txt && ed.value.innerHTML) ed.value.innerHTML = '';
  // 날짜는 가능하면 ISO로 저장 — 표시형은 toDateDisplay가 세션 dateFormat으로 그림
  if (isDate.value) {
    if (!txt) {
      fill.setValue(realKey.value, '');
      return;
    }
    const iso = toDateStorage(txt);
    fill.setValue(realKey.value, /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : txt);
    return;
  }
  fill.setValue(realKey.value, txt);
}

function onBlur() {
  if (!isDate.value || !ed.value) return;
  const txt = (ed.value.textContent || '').trim();
  if (!txt) {
    fill.setValue(realKey.value, '');
    return;
  }
  const iso = toDateStorage(txt);
  fill.setValue(realKey.value, iso);
  ed.value.textContent = toDateDisplay(iso);
}

function onTextClick() {
  fill.activeKey.value = realKey.value;
  nextTick(() => ed.value?.focus());
}

function markActive() {
  fill.activeKey.value = realKey.value;
}

function onDblClick(e: MouseEvent) {
  e.preventDefault();
  fill.activeKey.value = realKey.value;
  if (isDate.value) {
    dateMenu.value = true;
    return;
  }
  if (isLookup.value) {
    fill.activate(realKey.value);
    return;
  }
  if (isAddress.value) {
    fill.openAddress(realKey.value);
  }
}

function onAddressClick(e: MouseEvent) {
  e.preventDefault();
  e.stopPropagation();
  fill.activeKey.value = realKey.value;
  fill.openAddress(realKey.value);
}

function onLookupIconClick(e: MouseEvent) {
  e.preventDefault();
  e.stopPropagation();
  fill.activeKey.value = realKey.value;
  fill.activate(realKey.value);
}

function onDateIconClick(e: MouseEvent) {
  e.preventDefault();
  e.stopPropagation();
  fill.activeKey.value = realKey.value;
  dateMenu.value = true;
}
function clearDate() {
  fill.setValue(realKey.value, '');
  if (ed.value) ed.value.textContent = '';
  dateMenu.value = false;
}

function formatPickedDate(raw: unknown): string {
  // ISO(YYYY-MM-DD)로 저장 — 화면은 세션 dateFormat 표시(toDateDisplay), 생성은 백엔드가 ISO→세션 변환.
  const d = Array.isArray(raw) ? raw[0] : raw;
  if (!d) return '';
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) {
    return d.slice(0, 10);
  }
  const dt = d instanceof Date ? d : new Date(String(d));
  if (Number.isNaN(dt.getTime())) return String(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function onPickDate(raw: unknown) {
  const formatted = formatPickedDate(raw);
  if (!formatted) return;
  fill.setValue(realKey.value, formatted);
  dateMenu.value = false;
}

function onCurrencyUpdate(v: unknown) {
  fill.setValue(realKey.value, v == null ? '' : String(v).trim());
}

function onShareTypeUpdate(v: unknown) {
  fill.setValue(realKey.value, v == null ? '' : String(v).trim());
}
function onNationalityUpdate(v: unknown) {
  fill.setValue(realKey.value, v == null ? '' : String(v).trim());
}

const ynNormalized = computed(() => {
  const v = String(value.value || '').trim().toLowerCase();
  if (v === 'yes' || v === 'true' || v === 'y') return 'yes';
  if (v === 'no' || v === 'false' || v === 'n') return 'no';
  return '';
});

function setYesNo(val: 'yes' | 'no') {
  fill.activeKey.value = realKey.value;
  const next = ynNormalized.value === val ? '' : val === 'yes' ? 'Yes' : 'No';
  fill.setValue(realKey.value, next);
}

const tip = computed(() => {
  if (isDate.value) return 'Click 📅 to pick date';
  if (isAddress.value) return 'Click 📍 to enter address via chat';
  if (isSsic.value) return 'Click ✨ to look up SSIC / Business Activity';
  if (isLookup.value) return 'Click ✨ to look up Contact (auto-fills address, DOB, ID…)';
  if (isCurrency.value) return 'Select currency';
  if (isShareType.value) return 'Select type of shares';
  if (isNationality.value) return 'Select nationality';
  if (isYesNo.value) return 'Select Yes or No';
  return label.value;
});
</script>

<template>
  <span
    v-if="isYesNo"
    class="lv-field lv-yn"
    :data-key="props.fieldKey"
    :class="{ empty: !ynNormalized, filled: !!ynNormalized, active }"
    :title="tip"
  >
    <span class="lv-yn-opt" :class="{ on: ynNormalized === 'yes' }" @click.stop="setYesNo('yes')">
      <span class="box">{{ ynNormalized === 'yes' ? '✓' : '' }}</span> Yes
    </span>
    <span class="lv-yn-opt" :class="{ on: ynNormalized === 'no' }" @click.stop="setYesNo('no')">
      <span class="box">{{ ynNormalized === 'no' ? '✓' : '' }}</span> No
    </span>
  </span>

  <span
    v-else-if="isCurrency"
    class="lv-field lv-currency"
    :data-key="props.fieldKey"
    :class="{ empty: !value, filled: !!value, active }"
    :title="tip"
    @click.stop="markActive"
  >
    <v-combobox
      :model-value="value || null"
      :items="currencyItems"
      :placeholder="label"
      density="compact"
      variant="plain"
      hide-details
      class="lv-currency-combo"
      @update:model-value="onCurrencyUpdate"
      @focus="markActive"
    />
  </span>

  <span
    v-else-if="isShareType"
    class="lv-field lv-sharetype"
    :data-key="props.fieldKey"
    :class="{ empty: !value, filled: !!value, active }"
    :title="tip"
    @click.stop="markActive"
  >
    <v-combobox
      :model-value="value || null"
      :items="shareTypeItems"
      :placeholder="label"
      density="compact"
      variant="plain"
      hide-details
      class="lv-sharetype-combo"
      @update:model-value="onShareTypeUpdate"
      @focus="markActive"
    />
  </span>

  <!-- 국적 — CM 공통코드 B04. combobox 라 목록 선택 + 직접 입력 모두 가능 -->
  <span
    v-else-if="isNationality"
    class="lv-field lv-sharetype lv-nationality"
    :data-key="props.fieldKey"
    :class="{ empty: !value, filled: !!value, active }"
    :title="value || tip"
    @click.stop="markActive"
  >
    <v-combobox
      :model-value="value || null"
      :items="nationalityItems"
      :placeholder="label"
      density="compact"
      variant="plain"
      hide-details
      auto-select-first
      :menu-props="{ minWidth: 260, maxWidth: 360 }"
      class="lv-sharetype-combo"
      @update:model-value="onNationalityUpdate"
      @focus="markActive"
    />
  </span>

  <span
    v-else
    class="lv-field"
    :data-key="props.fieldKey"
    :class="{ empty: !value, filled: !!value, active, compact: props.compact }"
    :title="tip"
  >
    <span
      ref="ed"
      class="lv-text"
      contenteditable="true"
      :data-ph="props.compact ? '' : label"
      spellcheck="false"
      @input="onInput"
      @blur="onBlur"
      @click.stop="onTextClick"
      @dblclick.stop="onDblClick"
      @keydown.enter.prevent="ed?.blur()"
    />
    <button
      v-if="isDate"
      type="button"
      class="lv-chat lv-date"
      title="Pick date"
      @mousedown.prevent
      @click="onDateIconClick"
    >
      <v-icon size="12">mdi-calendar-month</v-icon>
    </button>
    <button
      v-if="isDate && value"
      type="button"
      class="lv-chat lv-clear"
      title="Clear date"
      @mousedown.prevent
      @click.stop="clearDate"
    >
      <v-icon size="12">mdi-close</v-icon>
    </button>
    <button
      v-if="isAddress"
      type="button"
      class="lv-chat lv-addr"
      title="Enter address via chat"
      @mousedown.prevent
      @click="onAddressClick"
    >
      <v-icon size="12">mdi-map-marker-outline</v-icon>
    </button>
    <button
      v-if="isLookup"
      type="button"
      class="lv-chat"
      :class="isSsic ? 'lv-ssic' : 'lv-ai'"
      :title="isSsic ? 'Look up SSIC via assistant' : 'Look up Contact via assistant'"
      @mousedown.prevent
      @click="onLookupIconClick"
    >
      <v-icon size="12">{{ isSsic ? 'mdi-briefcase-search-outline' : 'mdi-creation' }}</v-icon>
    </button>
    <v-menu
      v-if="isDate"
      v-model="dateMenu"
      :close-on-content-click="false"
      location="bottom"
    >
      <template #activator="{ props: menuProps }">
        <span class="lv-date-anchor" v-bind="menuProps" />
      </template>
      <v-date-picker hide-header @update:model-value="onPickDate" />
    </v-menu>
  </span>
</template>

<style scoped>
.lv-field {
  position: relative;
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  cursor: text;
  border-radius: 3px;
  padding: 0 3px;
  white-space: pre-line;
  vertical-align: baseline;
  max-width: 100%;
}
.lv-field.empty {
  background: #fff6d6;
  box-shadow: inset 0 -2px 0 #f2c94c;
}
.lv-field.empty .lv-text:empty::before {
  content: attr(data-ph);
  color: #9a7b1a;
  font-size: 0.92em;
  font-style: italic;
}
.lv-field.filled {
  background: #eaf3ff;
  box-shadow: inset 0 -2px 0 #1B84FF;
}
.lv-field.filled .lv-text {
  color: #10305c;
  font-weight: 600;
}
.lv-field.active {
  outline: 2px solid #1B84FF;
  outline-offset: 1px;
}
.lv-field.compact {
  padding: 0 1px;
  white-space: nowrap;
  max-width: 100%;
}
.lv-text {
  outline: none;
  min-width: 46px;
  overflow-wrap: break-word;
  word-break: break-word;
}
.lv-chat {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  flex: 0 0 auto;
  padding: 0;
}
.lv-chat.lv-addr { background: #fdeefe; color: #a23bb0; }
.lv-chat.lv-date { background: #eafaf1; color: #22a06b; }
.lv-chat.lv-clear { background: #f1f3f5; color: #6b7280; }
.lv-chat.lv-ai { background: #eaf3ff; color: #1b84ff; }
.lv-chat.lv-ssic { background: #fff4e5; color: #d97706; }
.lv-chat:hover { filter: brightness(0.94); }
.lv-date-anchor {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.lv-yn {
  gap: 10px;
  cursor: default;
  align-items: center;
  padding: 2px 6px;
}
.lv-yn-opt {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  font-size: 12px;
  color: #42506a;
  user-select: none;
}
.lv-yn-opt.on { color: #10305c; font-weight: 700; }
.lv-yn-opt .box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border: 1.5px solid #8b95a1;
  border-radius: 2px;
  font-size: 10px;
  line-height: 1;
  background: #fff;
}
.lv-yn-opt.on .box {
  border-color: #1B84FF;
  background: #eaf3ff;
  color: #1B84FF;
}

.lv-currency {
  min-width: 88px;
  width: 88px;
  max-width: 100px;
  flex: 0 0 88px;
  padding: 0 2px;
  cursor: pointer;
  vertical-align: middle;
  overflow: visible;
}
.lv-currency-combo { width: 100%; font-size: 12px; }
.lv-currency-combo :deep(.v-field) {
  --v-field-padding-start: 4px;
  --v-field-padding-end: 0px;
  min-height: 24px !important;
  font-size: 12px;
}
.lv-currency-combo :deep(.v-field__input) {
  min-height: 24px !important;
  min-width: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  font-size: 12px !important;
  color: #10305c;
  font-weight: 600;
  overflow: visible !important;
  text-overflow: clip !important;
  white-space: nowrap !important;
}
.lv-currency-combo :deep(input) {
  min-width: 2.8em !important;
  width: auto !important;
  overflow: visible !important;
  text-overflow: clip !important;
}
.lv-currency.empty .lv-currency-combo :deep(.v-field__input) {
  color: #9a7b1a;
  font-weight: 400;
  font-style: italic;
}
.lv-currency-combo :deep(.v-field__append-inner) { padding-top: 0; padding-inline-start: 0; }
.lv-currency-combo :deep(.v-field__clearable) { display: none; }
.lv-currency-combo :deep(.v-field__outline) { display: none; }

.lv-sharetype {
  min-width: 100px;
  width: 112px;
  max-width: 128px;
  flex: 0 0 112px;
  padding: 0 2px;
  cursor: pointer;
  vertical-align: middle;
  overflow: visible;
}
/**
 * 국적(B04)은 "Singapore Citizen" 처럼 이름이 길어 share type 용 112px 폭에서는 잘린다.
 * → 셀 폭을 채우도록 넓히고, 값 전체가 보이도록 줄바꿈을 허용한다.
 */
.lv-nationality {
  min-width: 150px;
  width: 100%;
  max-width: 100%;
  flex: 1 1 auto;
}
.lv-nationality .lv-sharetype-combo :deep(.v-field__input) {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
}
.lv-sharetype-combo { width: 100%; font-size: 12px; }
.lv-sharetype-combo :deep(.v-field) {
  --v-field-padding-start: 4px;
  --v-field-padding-end: 0px;
  min-height: 24px !important;
  font-size: 12px;
}
.lv-sharetype-combo :deep(.v-field__input) {
  min-height: 24px !important;
  min-width: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  font-size: 12px !important;
  color: #10305c;
  font-weight: 600;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}
.lv-sharetype-combo :deep(input) {
  min-width: 0 !important;
  width: 100% !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}
.lv-sharetype.empty .lv-sharetype-combo :deep(.v-field__input) {
  color: #9a7b1a;
  font-weight: 400;
  font-style: italic;
}
.lv-sharetype-combo :deep(.v-field__append-inner) { padding-top: 0; padding-inline-start: 0; }
.lv-sharetype-combo :deep(.v-field__clearable) { display: none; }
.lv-sharetype-combo :deep(.v-field__outline) { display: none; }
.lv-sharetype-combo :deep(.v-field--focused) {
  box-shadow: none !important;
}
.lv-sharetype-combo :deep(.v-field__overlay) {
  opacity: 0 !important;
}
</style>
